import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  deactivateAccount as apiDeactivateAccount,
  deleteAccount as apiDeleteAccount,
} from '../api/account';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  isActive?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string>;
  deactivateAccount: (password: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = getAuth();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [auth]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getIdToken = async (): Promise<string> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No user is signed in');
    }
    return currentUser.getIdToken();
  };

  // Reauthenticate the user with their password
  const reauthenticate = async (password: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      throw new Error('No user is signed in or email is missing');
    }

    const credential = EmailAuthProvider.credential(
      currentUser.email,
      password
    );
    await reauthenticateWithCredential(currentUser, credential);
  };

  // Deactivate account (soft delete)
  const deactivateAccount = async (password: string) => {
    setLoading(true);
    setError(null);
    try {
      // First reauthenticate the user
      await reauthenticate(password);

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is signed in');
      }

      // Get the auth token
      const token = await getIdToken();

      // Call API to deactivate account
      const response = await apiDeactivateAccount(currentUser.uid, token);

      if (response.error) {
        throw new Error(response.error);
      }

      // Update local user document to mark as inactive
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        isActive: false,
        deactivatedAt: new Date(),
      });

      // Sign out the user
      await signOut(auth);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete account (hard delete)
  const deleteAccount = async (password: string) => {
    setLoading(true);
    setError(null);
    try {
      // First reauthenticate the user
      await reauthenticate(password);

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is signed in');
      }

      // Call API to delete account
      const response = await apiDeleteAccount(currentUser.uid);

      if (response.error) {
        throw new Error(response.error);
      }
      await signOut(auth);
      // Delete the Firebase auth user
      // await deleteUser(currentUser);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    getIdToken,
    deactivateAccount,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
